import React from 'react';
import { Card } from '../ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../ui/accordion';
import { MapPin } from 'lucide-react';

const TripSummaryAccordion = ({
  formData,
  calculationResult,
  customPackageData,
  defaultOpen = false,
}) => {
  // Get route name with nights for each stop
  const getRouteName = () => {
    // For manual package, build route from manual_package_data.route
    if (formData.route_type === 'manual_package' && formData.manual_package_data?.route) {
      const route = formData.manual_package_data.route;
      const stopNames = route
        .map(s => `${s.place_name} (${s.nights}N)`)
        .join(' → ');
      return stopNames || 'Manual Package';
    }
    
    // For custom package, build route from stops with nights
    if (formData.route_type === 'custom_package' && customPackageData?.stops) {
      const arrivalCity = customPackageData.arrival_city || '';
      const departureCity = customPackageData.departure_city || arrivalCity;
      const stopNames = customPackageData.stops
        .map(s => `${s.place_name} (${s.nights}N)`)
        .join(' → ');
      return arrivalCity ? `${arrivalCity} → ${stopNames} → ${departureCity}` : stopNames;
    }
    
    // For predefined/custom routes, try to build from calculationResult.stops first
    if (calculationResult?.stops && calculationResult.stops.length > 0) {
      const arrivalCity = calculationResult.arrival_city || '';
      const departureCity = calculationResult.departure_city || arrivalCity;
      const stopNames = calculationResult.stops
        .map(s => `${s.place_name} (${s.nights}N)`)
        .join(' → ');
      if (arrivalCity && departureCity) {
        return `${arrivalCity} → ${stopNames} → ${departureCity}`;
      }
      return stopNames;
    }
    
    // For custom route type with custom_route in formData
    if (formData.route_type === 'custom' && formData.custom_route?.length > 0) {
      const arrivalCity = formData.arrival_city || '';
      const departureCity = formData.departure_city || arrivalCity;
      const stopNames = formData.custom_route
        .map(s => `${s.place_name} (${s.nights}N)`)
        .join(' → ');
      return arrivalCity ? `${arrivalCity} → ${stopNames} → ${departureCity}` : stopNames;
    }
    
    return calculationResult?.route_name || formData.route_name || 'Custom Route';
  };

  // Get total nights
  const getTotalNights = () => {
    // For manual package
    if (formData.route_type === 'manual_package' && formData.manual_package_data) {
      return formData.manual_package_data.total_nights || 
             formData.manual_package_data.route?.reduce((sum, s) => sum + (parseInt(s.nights) || 0), 0) || 0;
    }
    if (formData.route_type === 'custom_package' && customPackageData?.stops) {
      return customPackageData.stops.reduce((sum, s) => sum + (parseInt(s.nights) || 0), 0);
    }
    return calculationResult?.total_nights || formData.no_nights || 0;
  };

  // Get arrival date formatted
  const getArrivalDate = () => {
    const dateStr = formData.tour_start_date || formData.arrival_date || 
                    customPackageData?.arrival_date;
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString('en-IN', { 
        day: '2-digit', 
        month: 'short', 
        year: 'numeric' 
      });
    } catch {
      return dateStr;
    }
  };

  // Get arrival city
  const getArrivalCity = () => {
    return calculationResult?.arrival_city || 
           formData.arrival_city || 
           customPackageData?.arrival_city || 
           '-';
  };

  const totalNights = getTotalNights();
  const totalDays = totalNights + 1;
  
  // Room counts - prefer calculationResult, fallback to formData
  const singleRooms = calculationResult?.single_rooms ?? formData.room_config?.single_rooms ?? 0;
  const doubleRooms = calculationResult?.double_rooms ?? formData.room_config?.double_rooms ?? 0;
  const tripleRooms = calculationResult?.triple_rooms ?? formData.room_config?.triple_rooms ?? 0;
  const extraBeds = formData.room_config?.adult_extra_beds ?? calculationResult?.extra_adults ?? 0;
  // Child beds = children_with_bed count (they need beds)
  const childBeds = formData.no_children_with_bed || 
                    calculationResult?.children_with_bed || 
                    formData.room_config?.child_extra_beds || 
                    0;

  // Vehicle info - get from calculationResult.selected_vehicle first
  const selectedVehicle = calculationResult?.selected_vehicle;
  const vehicleType = selectedVehicle?.vehicle_type || 
                      selectedVehicle?.vehicle_label ||
                      calculationResult?.vehicle_type || 
                      formData.vehicle_type_selected || 
                      customPackageData?.vehicle_type || 
                      null;
  const vehicleCount = calculationResult?.vehicle_count || 
                       calculationResult?.no_of_vehicles || 
                       formData.no_of_vehicles || 
                       customPackageData?.no_of_vehicles || 
                       1;
  const transferDays = calculationResult?.transfer_days || 
                       formData.transfer_days || 
                       customPackageData?.transfer_days || 
                       totalDays;

  return (
    <Card className="border-navy/20">
      <Accordion type="single" collapsible defaultValue={defaultOpen ? "trip-summary" : undefined}>
        <AccordionItem value="trip-summary" className="border-none">
          <AccordionTrigger className="px-4 py-3 bg-navy/5 hover:bg-navy/10 rounded-t-lg">
            <span className="font-semibold text-navy text-sm flex items-center gap-2">
              <MapPin className="w-4 h-4" />
              Trip Summary
            </span>
          </AccordionTrigger>
          <AccordionContent className="px-4 py-3">
            <div className="space-y-4 text-sm">
              {/* Route & Passengers */}
              <div>
                <h5 className="font-semibold text-navy mb-1">Route & Passengers</h5>
                <div className="text-gray-600 space-y-0.5">
                  <div>Route: <span className="text-navy">{getRouteName()}</span></div>
                  <div>Duration: <span className="text-navy">{totalNights} Nights / {totalDays} Days</span></div>
                </div>
              </div>
              
              {/* Arrival Details */}
              <div>
                <h5 className="font-semibold text-navy mb-1">Arrival Details</h5>
                <div className="text-gray-600 space-y-0.5">
                  <div>
                    City: <span className="text-navy">{getArrivalCity()}</span> | 
                    Date: <span className="text-navy">{getArrivalDate()}</span> | 
                    Point: <span className="text-navy">{formData.arrival_point || '-'}</span>
                  </div>
                </div>
              </div>
              
              {/* Passenger Details */}
              <div>
                <h5 className="font-semibold text-navy mb-1">Passenger Details</h5>
                <div className="text-gray-600 space-y-0.5">
                  <div>
                    Adults: <span className="text-navy">{formData.no_adults || 0}</span> | 
                    Children (bed): <span className="text-navy">{formData.no_children_with_bed || 0}</span> | 
                    Children (no bed): <span className="text-navy">{formData.no_children_no_bed || 0}</span>
                  </div>
                </div>
              </div>
              
              {/* Room Configuration */}
              <div>
                <h5 className="font-semibold text-navy mb-1">Room Configuration</h5>
                <div className="text-gray-600 space-y-0.5">
                  <div>
                    Single: <span className="text-navy">{singleRooms}</span> | 
                    Double: <span className="text-navy">{doubleRooms}</span> | 
                    Triple: <span className="text-navy">{tripleRooms}</span>
                  </div>
                </div>
              </div>
              
              {/* Bed Configuration */}
              <div>
                <h5 className="font-semibold text-navy mb-1">Bed Configuration</h5>
                <div className="text-gray-600 space-y-0.5">
                  <div>
                    Extra Beds: <span className="text-navy">{extraBeds}</span> | 
                    Child Beds: <span className="text-navy">{childBeds}</span>
                  </div>
                </div>
              </div>
              
              {/* Vehicle */}
              {vehicleType && (
                <div>
                  <h5 className="font-semibold text-navy mb-1">Vehicle</h5>
                  <div className="text-gray-600 space-y-0.5">
                    <div>
                      Type: <span className="text-navy">{vehicleCount}× {vehicleType}</span> | 
                      Days: <span className="text-navy">{transferDays}</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </Card>
  );
};

export default TripSummaryAccordion;
